/**
 * 子パターン（#2967）の入力バリデーション。
 *
 * 保存前に画面側で使う。
 */

import {
  SUB_PATTERN_NAME_MAX_LENGTH,
  SUB_PATTERN_DESCRIPTION_MAX_LENGTH,
} from '../../constants/enum';
import { subPatternErrorMessage } from '../../constants/subPattern';
import type { ItemOverrideType, SubPatternType } from '../../types/subPattern';
import { countActiveOverrides } from './subPatternConverter';

type ValidateSubPatternInput = {
  subPatternName: string;
  description: string;
  itemOverrides: ItemOverrideType[];
  /** 同一パターンの既存の子パターン。名称の重複判定に使う */
  existingSubPatterns: SubPatternType[];
  /** 編集中の子パターンID。自分自身を重複判定から除くために渡す（新規作成時は undefined） */
  editingSubPatternId?: string;
};

/**
 * 子パターンの入力内容を検証する。
 * エラーがなければ null を返す（validateChoices と同じ流儀）。
 */
export const validateSubPattern = (input: ValidateSubPatternInput): string | null => {
  const name = (input.subPatternName ?? '').trim();
  if (name === '') return subPatternErrorMessage.emptyName;
  if (name.length > SUB_PATTERN_NAME_MAX_LENGTH) {
    return subPatternErrorMessage.nameTooLong;
  }

  // 説明文は仕分け判定のプロンプトに入る。空欄だと判定の手がかりが無く、
  // その子パターンが一度も選ばれない状態になるため必須にする
  const description = (input.description ?? '').trim();
  if (description === '') return subPatternErrorMessage.emptyDescription;
  if (description.length > SUB_PATTERN_DESCRIPTION_MAX_LENGTH) {
    return subPatternErrorMessage.descriptionTooLong;
  }

  // 名称が重複すると、結果一覧のバッジと絞り込みでどちらの子パターンか区別できなくなる。
  const isDuplicated = input.existingSubPatterns.some(
    subPattern =>
      subPattern.subPatternId !== input.editingSubPatternId &&
      (subPattern.sub_pattern_name ?? '').trim() === name,
  );
  if (isDuplicated) return subPatternErrorMessage.duplicatedName;

  // 上書きが1件も無い子パターンは、判定に成功しても親と同じ読み取りにしかならない。
  if (countActiveOverrides(input.itemOverrides) === 0) {
    return subPatternErrorMessage.emptyOverrides;
  }

  return null;
};
